import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography, } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { alpha, useTheme } from '@mui/material/styles';
import NavBar from '../NavBar/NavBar';
import { PageLoadScreen } from '../../library/loading/loading';
import { loadEnvConfig, readAgencyContactsGridId, readAgencyContactsGridPageSize, readSidebarBase, } from '../../../services/envConfig';
import { fetchAllGridRows } from '../../../services/gridDataFetcher';
import { isInactiveSystemStatusRow } from '../../../utils/systemStatus';
import { deriveGridColumns, toGridCellRows } from '../../../utils/gridColumns';
import { resolvePublicUrl } from '../../../utils/publicUrl';
import { cmsPortalTypography } from '../../../theme/cmsTypography';
import { portalContentMaxWidthSx, portalPageHorizontalPaddingSx } from '../../../theme/portalLayout';
/** Hero text and table share one centered column so the grid keeps side gutters on wide screens. */
const CONTENT_MAX_WIDTH = 1280;
/**
 * Agency Contacts — searchable, sortable table of the Flow Builder grid in `env.json` `AgencyContactsGridId`.
 * Columns, headers and widths are derived from the grid response; page copy comes from `pages/agency-contacts.json`.
 */
const AgencyContactsPage = ({ nav, heading, subheading, backgroundImage, searchPlaceholder, searchButtonLabel, emptyText, errorText, rowsPerPageOptions = [10, 25, 50], }) => {
    const theme = useTheme();
    const cms = useMemo(() => cmsPortalTypography(theme), [theme]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0] ?? 10);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const env = await loadEnvConfig();
                const sidebar = readSidebarBase(env);
                const gridId = readAgencyContactsGridId(env);
                if (!sidebar || !gridId)
                    throw new Error('Agency contacts grid is not configured');
                const raw = await fetchAllGridRows(sidebar, gridId, readAgencyContactsGridPageSize(env));
                if (!cancelled)
                    setRows(toGridCellRows(raw.filter((r) => !isInactiveSystemStatusRow(r))));
            }
            catch {
                if (!cancelled)
                    setFailed(true);
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    const columns = useMemo(() => deriveGridColumns(rows), [rows]);
    const visibleRows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? rows.filter((r) => columns.some((c) => r[c.field]?.toLowerCase().includes(q)))
            : rows;
        if (!sort)
            return filtered;
        const dir = sort.direction === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const av = a[sort.field] ?? '';
            const bv = b[sort.field] ?? '';
            /** Blank cells always sort last, regardless of direction. */
            if (!av !== !bv)
                return av ? -1 : 1;
            return dir * av.localeCompare(bv, undefined, { sensitivity: 'base' });
        });
    }, [rows, columns, query, sort]);
    const pagedRows = visibleRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const toggleSort = (field) => {
        setSort((s) => s?.field === field ? { field, direction: s.direction === 'asc' ? 'desc' : 'asc' } : { field, direction: 'asc' });
        setPage(0);
    };
    /** Filtering is live while typing; submit only keeps the form from reloading the page. */
    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
    };
    const heroSrc = resolvePublicUrl(backgroundImage);
    const gridLineColor = alpha(theme.palette.primary.main, 0.14);
    const contentSx = { ...portalContentMaxWidthSx, ...portalPageHorizontalPaddingSx, maxWidth: CONTENT_MAX_WIDTH };
    return (_jsxs(Box, { component: "main", sx: { bgcolor: alpha(theme.palette.primary.light, 0.03), minHeight: '100%' }, children: [_jsxs(Box, { sx: { position: 'relative', overflow: 'hidden', bgcolor: alpha(theme.palette.primary.main, 0.08) }, children: [heroSrc ? (_jsx(Box, { component: "img", src: heroSrc, alt: "", "aria-hidden": true, sx: {
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'right center',
                        } })) : null, _jsxs(Box, { sx: { position: 'relative', zIndex: 1 }, children: [nav ? _jsx(NavBar, { ...nav, embedInHero: true }) : null, _jsxs(Box, { sx: {
                                    ...contentSx,
                                    py: { xs: 3.5, md: 5 },
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: { xs: 2.5, md: 4 },
                                    alignItems: 'center',
                                }, children: [_jsxs(Box, { sx: { textAlign: { xs: 'center', md: 'left' } }, children: [_jsx(Typography, { variant: "h3", component: "h1", sx: {
                                                    color: 'primary.dark',
                                                    fontWeight: 800,
                                                    lineHeight: 1.2,
                                                    m: 0,
                                                }, children: heading }), subheading ? (_jsx(Typography, { variant: "subtitle1", sx: { color: 'text.secondary', mt: 0.75, fontWeight: 500, lineHeight: 1.45 }, children: subheading })) : null] }), _jsxs(Box, { component: "form", role: "search", onSubmit: handleSearch, sx: {
                                            display: 'flex',
                                            alignItems: 'stretch',
                                            bgcolor: alpha(theme.palette.background.paper, 0.97),
                                            backdropFilter: 'blur(10px)',
                                            borderRadius: { xs: 2.5, sm: 3 },
                                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.dark, 0.22)}`,
                                            overflow: 'hidden',
                                            border: `1px solid ${alpha(theme.palette.primary.contrastText, 0.2)}`,
                                        }, children: [_jsx(TextField, { fullWidth: true, placeholder: searchPlaceholder, value: query, onChange: (e) => {
                                                    setQuery(e.target.value);
                                                    setPage(0);
                                                }, variant: "standard", inputProps: { 'aria-label': searchPlaceholder }, InputProps: {
                                                    disableUnderline: true,
                                                    startAdornment: (_jsx(InputAdornment, { position: "start", children: _jsx(SearchIcon, { sx: { color: 'text.secondary', ml: 0.5, mr: 0.5, fontSize: 22 } }) })),
                                                    sx: { pl: 1.35, pr: 1.75, py: 0.95, ...cms.searchFieldInput },
                                                }, sx: { flex: 1, m: 0 } }), searchButtonLabel ? (_jsx(Button, { type: "submit", variant: "contained", color: "secondary", sx: {
                                                    borderRadius: 0,
                                                    px: { xs: 2.5, sm: 3.5 },
                                                    minWidth: { xs: 100, sm: 120 },
                                                    boxShadow: 'none',
                                                    ...cms.searchFieldButton,
                                                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark ?? theme.palette.secondary.main} 100%)`,
                                                    '&:hover': { boxShadow: 'none', filter: 'brightness(1.05)' },
                                                }, children: searchButtonLabel })) : null] })] })] })] }), _jsx(Box, { sx: { ...contentSx, py: { xs: 3, md: 5 } }, children: loading ? (_jsx(PageLoadScreen, { variant: "embedded" })) : failed ? (_jsx(Typography, { role: "alert", sx: { color: 'error.main', py: 4, textAlign: 'center' }, children: errorText })) : (_jsxs(Paper, { elevation: 0, sx: {
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.12),
                        boxShadow: `0 10px 36px ${alpha(theme.palette.primary.dark, 0.08)}`,
                        overflow: 'hidden',
                        p: { xs: 1.5, md: 2.5 },
                    }, children: [_jsx(TableContainer, { sx: {
                                border: '1px solid',
                                borderColor: gridLineColor,
                                borderRadius: 2,
                            }, children: _jsxs(Table, { size: "small", "aria-label": heading, sx: {
                                    /** Below this the container scrolls horizontally instead of crushing columns (phones/tablets). */
                                    minWidth: 900,
                                    '& .MuiTableCell-root': { borderBottom: `1px solid ${gridLineColor}` },
                                }, children: [_jsx(TableHead, { children: _jsx(TableRow, { children: columns.map((c) => (_jsx(TableCell, { sortDirection: sort?.field === c.field ? sort.direction : false, sx: {
                                                    bgcolor: 'primary.dark',
                                                    color: 'common.white',
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    /** Proportional to the column's content, so the table fills its width without scrolling. */
                                                    width: `${c.widthPercent}%`,
                                                    py: 1.5,
                                                    px: 2,
                                                    '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root:hover, & .MuiTableSortLabel-root.Mui-active': { color: 'common.white' },
                                                    '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                                                }, children: _jsx(TableSortLabel, { active: sort?.field === c.field, direction: sort?.field === c.field ? sort.direction : 'asc', onClick: () => toggleSort(c.field), children: c.header }) }, c.field))) }) }), _jsx(TableBody, { children: pagedRows.length === 0 ? (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: columns.length, sx: { textAlign: 'center', py: 5, color: 'text.secondary' }, children: emptyText }) })) : (pagedRows.map((r, i) => (_jsx(TableRow, { hover: true, sx: {
                                                '&:nth-of-type(even)': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                                                '&:last-child td': { borderBottom: 0 },
                                            }, children: columns.map((c) => {
                                                const v = r[c.field];
                                                return (_jsx(TableCell, { sx: { py: 1.25, px: 2, verticalAlign: 'top' }, children: !v ? (_jsx(Box, { component: "span", sx: { color: 'text.disabled' }, children: "\u2014" })) : c.isEmail ? (_jsx(Link, { href: `mailto:${v}`, underline: "hover", sx: { whiteSpace: 'nowrap' }, children: v })) : (v) }, c.field));
                                            }) }, `${page}-${i}`)))) })] }) }), _jsx(TablePagination, { component: "div", count: visibleRows.length, page: page, onPageChange: (_, p) => setPage(p), rowsPerPage: rowsPerPage, rowsPerPageOptions: rowsPerPageOptions, onRowsPerPageChange: (e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }, sx: { mt: 0.5 } })] })) })] }));
};
export default AgencyContactsPage;
